import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Typography, Switch, Checkbox, Space, Spin } from "antd";
import userService from "../../services/userService";
import { useCommon } from "../../context/CommonContext";
const { Text } = Typography;

function MenuAccess() {
  const { role_id } = useParams();
  const { setPageTitle } = useCommon();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);

  const getAllMenu = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllMenu(role_id);
      const data = response?.data?.data || [];
      const safeMenus = Array.isArray(data) ? data : [];
      setMenus(safeMenus);
      // Keep backend data structure intact - no transformation needed
    } catch (err) {
      setMenus([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageTitle("Menu Access for " + role_id);
    getAllMenu();
  }, [role_id]);

  const childApiCall = async (payload) => {
    try {
      const response = await userService.updateMenuAccess(role_id, payload);
      return response.data.status;
    } catch (error) {
      console.error("Error updating access:", error);
      return false;
    }
  };

  /**
   * Toggle parent
   * Updates parent + all children in backend
   */
  const onToggleParent = (parentId) => async (checked) => {
    const currentMenus = [...menus];
    const parent = menus.find((menu) => menu.id === parentId);

    // Build updated menus for UI
    const updatedMenus = menus.map((p) =>
      p.id === parentId
        ? {
            ...p,
            permissions: { ...p.permissions, is_enabled: checked },
            child_menus: (p.child_menus || []).map((c) => ({
              ...c,
              permissions: {
                ...c.permissions,
                is_enabled: checked,
                ...(checked
                  ? {}
                  : {
                      can_read: false,
                      can_create: false,
                      can_update: false,
                      can_delete: false,
                    }),
              },
            })),
          }
        : p
    );

    // Optimistic UI update
    setMenus(updatedMenus);

    // Call API for parent + all children
    const payloads = [
      {
        ...parent,
        permissions: { ...parent.permissions, is_enabled: checked },
      },
      ...(parent.child_menus || []).map((c) => ({
        ...c,
        permissions: {
          ...c.permissions,
          is_enabled: checked,
          ...(checked
            ? {}
            : {
                can_read: false,
                can_create: false,
                can_update: false,
                can_delete: false,
              }),
        },
      })),
    ];

    const results = await Promise.all(payloads.map((p) => childApiCall(p)));

    // Rollback if any failed
    if (results.some((r) => !r)) {
      setMenus(currentMenus);
    }
  };

  /**
   * Toggle child
   * Updates child + parent in backend
   */
  const onToggleChild = (parentId, childId) => async (checked) => {
    const currentMenus = [...menus];
    const parent = menus.find((menu) => menu.id === parentId);

    const updatedChildren = (parent.child_menus || []).map((child) =>
      child.id === childId
        ? {
            ...child,
            permissions: {
              ...child.permissions,
              is_enabled: checked,
              ...(checked
                ? {}
                : {
                    can_read: false,
                    can_create: false,
                    can_update: false,
                    can_delete: false,
                  }),
            },
          }
        : child
    );

    const hasEnabledChildren = updatedChildren.some(
      (c) => c.permissions?.is_enabled
    );

    const updatedMenus = menus.map((p) =>
      p.id === parentId
        ? {
            ...p,
            permissions: { ...p.permissions, is_enabled: hasEnabledChildren },
            child_menus: updatedChildren,
          }
        : p
    );

    // Optimistic UI update
    setMenus(updatedMenus);

    const child = updatedChildren.find((c) => c.id === childId);

    // Call API for child + parent
    const payloads = [
      {
        ...child,
        permissions: { ...child.permissions, is_enabled: checked },
      },
      {
        ...parent,
        permissions: {
          ...parent.permissions,
          is_enabled: hasEnabledChildren,
        },
      },
    ];

    const results = await Promise.all(payloads.map((p) => childApiCall(p)));

    // Rollback if failed
    if (results.some((r) => !r)) {
      setMenus(currentMenus);
    }
  };

  const onPermissionChange = (parentId, childId, field) => async (e) => {
    const checked = e?.target ? e.target.checked : !!e;
    const currentMenus = [...menus];
  
    // Update state optimistically
    setMenus((prev) =>
      prev.map((parent) => {
        if (parent.id !== parentId) return parent;
  
        if (!parent.child_menus || parent.child_menus.length === 0) {
          return {
            ...parent,
            permissions: {
              ...parent.permissions,
              [field]: checked,
            },
          };
        }
  
        // Else update the child permissions
        const updatedChildren = parent.child_menus.map((child) =>
          child.id === childId
            ? {
                ...child,
                permissions: {
                  ...child.permissions,
                  [field]: checked,
                },
              }
            : child
        );
  
        const hasEnabledChildren = updatedChildren.some((child) => {
          const p = child.permissions || {};
          return (
            p.is_enabled &&
            (p.can_create || p.can_read || p.can_update || p.can_delete)
          );
        });
  
        return {
          ...parent,
          permissions: {
            ...parent.permissions,
            is_enabled: hasEnabledChildren,
          },
          child_menus: updatedChildren,
        };
      })
    );
  
    // Determine if it’s a parent-only update or a child update
    const parent = menus.find((menu) => menu.id === parentId);
  
    let payload;
    if (!parent.child_menus || parent.child_menus.length === 0) {
      // Parent has no children
      payload = {
        ...parent,
        permissions: {
          ...parent.permissions,
          [field]: checked,
        },
      };
    } else {
      // Update child
      const child = parent.child_menus.find((child) => child.id === childId);
      if (!child) return; // safety guard
      payload = {
        ...child,
        permissions: {
          ...child.permissions,
          [field]: checked,
        },
      };
    }
  
    const result = await childApiCall(payload);
    if (!result) {
      setMenus(currentMenus);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin />;
      </div>
    );
  }

  return (
    <>
      <div className="p-6" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {menus.map((menu) => {
          const parentEnabled = Boolean(menu.permissions?.is_enabled);
          const permissions = menu.permissions || {};
          const childMenus = menu.child_menus || [];

          return (
            <Card
              size="small"
              title={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                  }}
                >
                  <Switch
                    size="small"
                    checked={parentEnabled}
                    onChange={onToggleParent(menu.id)}
                  />
                  <Text>{menu.name}</Text>
                </div>
              }
              extra={
                childMenus.length === 0 ? (
                  <Space size={12} wrap>
                    <Checkbox
                      checked={Boolean(permissions.can_read)}
                      disabled={!parentEnabled}
                      onChange={onPermissionChange(
                        menu.id,
                        menu.id,
                        "can_read"
                      )}
                    >
                      Read
                    </Checkbox>
                    <Checkbox
                      checked={Boolean(permissions.can_create)}
                      disabled={!parentEnabled}
                      onChange={onPermissionChange(
                        menu.id,
                        menu.id,
                        "can_create"
                      )}
                    >
                      Create
                    </Checkbox>
                    <Checkbox
                      checked={Boolean(permissions.can_update)}
                      disabled={!parentEnabled}
                      onChange={onPermissionChange(
                        menu.id,
                        menu.id,
                        "can_update"
                      )}
                    >
                      Update
                    </Checkbox>
                    <Checkbox
                      checked={Boolean(permissions.can_delete)}
                      disabled={!parentEnabled}
                      onChange={onPermissionChange(
                        menu.id,
                        menu.id,
                        "can_delete"
                      )}
                    >
                      Delete
                    </Checkbox>
                  </Space>
                ) : null
              }
            >
              <div>
                {(menu.child_menus || []).length > 0 ? (
                  <ul style={{ paddingLeft: 0, margin: 0, listStyle: "none" }}>
                    {menu.child_menus.map((child) => {
                      const permissions = child.permissions || {};
                      const childEnabled = Boolean(permissions.is_enabled);

                      return (
                        <li
                          key={child.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            padding: "6px 0",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <Switch
                              checked={childEnabled}
                              onChange={onToggleChild(menu.id, child.id)}
                              size="small"
                            />
                            <Text>{child.name}</Text>
                          </div>
                          <div>
                            <Space size={12} wrap>
                              <Checkbox
                                checked={Boolean(permissions.can_read)}
                                disabled={!childEnabled}
                                onChange={onPermissionChange(
                                  menu.id,
                                  child.id,
                                  "can_read"
                                )}
                              >
                                Read
                              </Checkbox>
                              <Checkbox
                                checked={Boolean(permissions.can_create)}
                                disabled={!childEnabled}
                                onChange={onPermissionChange(
                                  menu.id,
                                  child.id,
                                  "can_create"
                                )}
                              >
                                Create
                              </Checkbox>
                              <Checkbox
                                checked={Boolean(permissions.can_update)}
                                disabled={!childEnabled}
                                onChange={onPermissionChange(
                                  menu.id,
                                  child.id,
                                  "can_update"
                                )}
                              >
                                Update
                              </Checkbox>
                              <Checkbox
                                checked={Boolean(permissions.can_delete)}
                                disabled={!childEnabled}
                                onChange={onPermissionChange(
                                  menu.id,
                                  child.id,
                                  "can_delete"
                                )}
                              >
                                Delete
                              </Checkbox>
                            </Space>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <Text type="secondary">No child menus</Text>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );
}

export default MenuAccess;
